/**
 * Core sync epic: processes the offline queue sequentially when the app
 * comes back online. Also syncs on startup if items are pending.
 *
 * @author Milton Filho
 */
import { Observable, EMPTY, concat, defer, from, merge, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap } from 'rxjs/operators';
import { CustomEpic, AnyPayloadAction, Appointment } from 'types/types';
import {
  ConnectionStatus,
  getCurrentConnectionStatus,
  subscribe,
} from 'utils/connectionStatus';
import {
  bookingConflicted,
  syncStart,
  syncComplete,
  syncItemSuccess,
  syncItemFailure,
  syncBookingSuccess,
} from 'modules/offlineSync/actions';
import { updateAppointmentSuccess } from 'modules/appointments/actions';
import { OfflineActionKind, OfflineQueueItem } from 'modules/offlineSync/types';
import { getUpdateAppointmentUrl } from 'modules/appointments/utils/getUpdateAppointmentUrl';
import { getAuthHeaders } from 'api/getAuthHeaders';
import { APPOINTMENTS_ENDPOINT } from 'modules/appointments/constants';
import {
  updateAppointmentCallTranslator,
  bookAppointmentCallTranslator,
} from 'modules/appointments/translator';
import { BookAppointmentResponse } from 'modules/appointments/actions';
import { GlobalState } from 'reducers';
import { StateObservable } from 'redux-observable';
import { EpicDependencies } from 'store/middleware';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { extractApiErrorMessage } from 'utils/epics/extractApiErrorMessage';
import { BOOKING_CONFLICT_STATUS } from 'modules/offlineSync/constants';
import { getIdempotencyHeaders } from 'utils/api/getIdempotencyHeaders';

/**
 * Resolve an error message for a failed offline sync item, using the
 * backend-provided body message first and falling back to the Axios
 * wrapper message, with a final safety net if neither is present.
 */
const getApiErrorMessage = (err: AxiosError): string =>
  extractApiErrorMessage(err) || err?.message || 'Unknown error';

/**
 * A queue item this epic cannot possibly send.
 *
 * Every item `enqueueOfflineItem` produces has a `payload`, so the only way to
 *   reach this is persisted state that does not match the code reading it —
 *   written by an earlier build, or truncated. The whole redux state is
 *   serialised to `localStorage` on every action and rehydrated with no schema
 *   version, so "shaped like an older release" is a reachable state rather than
 *   a hypothetical one.
 *
 * Reading through it used to throw. That mattered far more than the item itself:
 *   in `redux-observable` an error reaching the merged output **kills the epic**,
 *   so one malformed entry ended offline sync for that user permanently — no
 *   arrivals, no bookings, until someone cleared their storage.
 */
const isUnsendable = (item: OfflineQueueItem): boolean => {
  if (!item?.id || !item.payload) return true;
  if (item.kind === OfflineActionKind.StartSession) {
    return item.payload.id == null;
  }
  return false;
};

const processSyncItem = (
  item: OfflineQueueItem,
  state$: StateObservable<GlobalState>,
  postApi$: EpicDependencies['postApi$'],
): Observable<AnyPayloadAction> => {
  const headers = getAuthHeaders(state$.value);

  if (isUnsendable(item)) {
    // No toast. A therapist cannot act on corrupt local state, and this fires on
    //   every reconnect and every app start — the existing failure toasts are
    //   `duration: Infinity`, so one would sit on screen for a problem nobody
    //   can address. Marking it keeps the record in state where it is
    //   inspectable.
    //
    // The item is marked rather than dropped: removing queued work is the
    //   pending decision about `failedAt` (nothing dispatches
    //   `CLEAR_FAILED_ITEM` yet), and this fix is only meant to stop the epic
    //   dying. Without an `id` there is nothing to mark, so it is skipped.
    // Identity only, not the item. Its payload carries appointment and patient
    //   ids, and `console.error` reaches Sentry as a breadcrumb with its raw
    //   arguments attached — see `utils/logging/scrubBreadcrumb`.
    console.error('Skipping an offline queue item that cannot be sent', {
      id: item?.id,
      kind: item?.kind,
      createdAt: item?.createdAt,
      hasPayload: !!item?.payload,
    });
    return item?.id
      ? of(
          syncItemFailure({
            queueItemId: item.id,
            error: 'Malformed queue item',
          }),
        )
      : EMPTY;
  }

  if (item.kind === OfflineActionKind.StartSession) {
    const appointmentId = item.payload.id as number;
    return postApi$({
      endpoint: getUpdateAppointmentUrl({ appointmentId }),
      payload: item.payload as Record<string, string>,
      headers,
    }).pipe(
      map(response => {
        const appointment = updateAppointmentCallTranslator(response.data);
        return [
          syncItemSuccess({
            queueItemId: item.id,
            // `?? []` for the same reason as `isUnsendable`: a rehydrated item
            //   may predate this field, and `pendingIds.filter` would then
            //   dereference `undefined` inside the reducer.
            pendingIdsToRemove: item.tempAppointmentIds ?? [],
          }),
          updateAppointmentSuccess(appointment),
        ];
      }),
      switchMap(actions => from(actions)),
      catchError((err: AxiosError) => {
        const message = getApiErrorMessage(err);
        toast.error(`Failed to sync session arrival: ${message}`, {
          duration: Infinity,
        });
        return of(
          syncItemFailure({
            queueItemId: item.id,
            error: message,
          }),
        );
      }),
    );
  }

  if (item.kind === OfflineActionKind.BookAppointment) {
    return postApi$({
      endpoint: APPOINTMENTS_ENDPOINT,
      payload: item.payload as Record<string, string>,
      // The queue item's own id is the idempotency key. It is minted with
      //   `randomUUID()` when the intent is enqueued, persisted with the item
      //   and identical on every replay — which is exactly the contract, and
      //   needs no new field, so items written by earlier builds carry a usable
      //   key too.
      headers: { ...headers, ...getIdempotencyHeaders(item.id) },
    }).pipe(
      map(response => {
        // The translator's fallback branch returns a legacy single-Appointment
        // shape for older backend responses, which is wider than the declared
        // BookAppointmentResponse. Widen the local view so the `in` narrowing
        // below is sound.
        const translated = bookAppointmentCallTranslator(response.data) as
          | BookAppointmentResponse
          | Appointment;
        let realAppointments: Appointment[];
        if ('appointments' in translated) {
          realAppointments = Array.isArray(translated.appointments)
            ? translated.appointments
            : [];
        } else {
          realAppointments = [translated];
        }

        return [
          syncBookingSuccess({
            queueItemId: item.id,
            tempIds: item.tempAppointmentIds ?? [],
            realAppointments,
          }),
        ];
      }),
      switchMap(actions => from(actions)),
      catchError((err: AxiosError) => {
        // A conflict is permanent: the appointment it collides with is not
        //   going away, so replaying can only collide again. Retrying it was
        //   how one slot came to hold seven real appointments — see
        //   `resolveBookingConflict$`, which decides what to do with the
        //   optimistic card and is deliberately not this epic's job: it needs a
        //   schedule refetch to finish first, and this runs inside a
        //   `concatMap` over the whole queue.
        //
        // No `syncItemFailure` here. That would stamp `failedAt`, and whether
        //   the item had *already* failed is the only signal telling the two
        //   causes of a conflict apart, so it is read now and carried along.
        if (err?.response?.status === BOOKING_CONFLICT_STATUS) {
          return of(
            bookingConflicted({
              queueItemId: item.id,
              tempAppointmentIds: item.tempAppointmentIds ?? [],
              appDatetime: String(item.payload.app_datetime ?? ''),
              patientId: Number(item.payload.patient_id ?? 0),
              hadFailedBefore: !!item.failedAt,
            }),
          );
        }

        const message = getApiErrorMessage(err);
        toast.error(`Failed to sync offline booking: ${message}`, {
          duration: Infinity,
        });
        return of(
          syncItemFailure({
            queueItemId: item.id,
            error: message,
          }),
        );
      }),
    );
  }

  return EMPTY;
};

export const syncOnReconnect$: CustomEpic = (
  _action$,
  state$,
  { postApi$ },
) => {
  const connectionStatus$ = new Observable<ConnectionStatus>(subscriber => {
    const unsub = subscribe(status => subscriber.next(status));
    return unsub;
  });

  const processQueue = () => {
    const { queue, syncStatus } = state$.value.offlineSync;
    if (queue.length === 0 || syncStatus === 'syncing') return EMPTY;

    const toastId = toast.loading('Syncing offline changes...');

    return concat(
      of(syncStart()),
      from(queue).pipe(
        concatMap(item => processSyncItem(item, state$, postApi$)),
      ),
      defer(() => {
        toast.dismiss(toastId);
        return of(syncComplete());
      }),
    );
  };

  // Sync when coming back online
  const reconnectSync$ = connectionStatus$.pipe(
    filter(status => status === 'online'),
    switchMap(() => processQueue()),
  );

  // Sync pending items on app startup
  const initialSync$ = defer(() => {
    if (getCurrentConnectionStatus() === 'online') {
      return processQueue();
    }
    return EMPTY;
  });

  return merge(initialSync$, reconnectSync$);
};
